import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './BreakoutAdvanced.css';

const BreakoutAdvanced = () => {
  const [paddle, setPaddle] = useState({ x: 150, width: 100 });
  const [ball, setBall] = useState({ x: 200, y: 200, dx: 4, dy: 4 });
  const [bricks, setBricks] = useState([]);
  const [powerUps, setPowerUps] = useState([]);
  const [score, setScore] = useState(0);
  const [gameActive, setGameActive] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [keys, setKeys] = useState({});
  const gameAreaRef = useRef(null);
  const animationRef = useRef(null);

  const GAME_WIDTH = 400;
  const GAME_HEIGHT = 500;
  const PADDLE_HEIGHT = 10;
  const BALL_SIZE = 10;
  const BRICK_ROWS = 5;
  const BRICK_COLS = 8;
  const PADDLE_SPEED = 7;

  const initializeBricks = () => {
    const newBricks = [];
    const colors = ['#e74c3c', '#e67e22', '#f39c12', '#2ecc71', '#3498db'];
    for (let row = 0; row < BRICK_ROWS; row++) {
      for (let col = 0; col < BRICK_COLS; col++) {
        newBricks.push({
          x: col * 47 + 10,
          y: row * 25 + 50,
          width: 45,
          height: 20,
          destroyed: false,
          color: colors[row],
          hasPowerUp: Math.random() < 0.2
        });
      }
    }
    return newBricks;
  };

  const startGame = () => {
    setGameActive(true);
    setGameOver(false);
    setGameWon(false);
    setScore(0);
    setPaddle({ x: 150, width: 100 });
    setBall({ x: 200, y: 200, dx: 4, dy: 4 });
    setBricks(initializeBricks());
    setPowerUps([]);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key.toLowerCase();
      if (['a', 'd', 'arrowleft', 'arrowright'].includes(key)) {
        e.preventDefault();
        setKeys(k => ({ ...k, [key]: true }));
      }
    };

    const handleKeyUp = (e) => {
      const key = e.key.toLowerCase();
      setKeys(k => ({ ...k, [key]: false }));
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  useEffect(() => {
    if (!gameActive || gameOver || gameWon) return;

    const gameLoop = () => {
      if (keys['a'] || keys['arrowleft']) {
        setPaddle(p => ({ ...p, x: Math.max(0, p.x - PADDLE_SPEED) }));
      }
      if (keys['d'] || keys['arrowright']) {
        setPaddle(p => ({ ...p, x: Math.min(GAME_WIDTH - p.width, p.x + PADDLE_SPEED) }));
      }

      setBall(b => {
        let newBall = { ...b };
        newBall.x += newBall.dx;
        newBall.y += newBall.dy;

        if (newBall.x <= 0 || newBall.x >= GAME_WIDTH - BALL_SIZE) {
          newBall.dx = -newBall.dx;
        }
        if (newBall.y <= 0) {
          newBall.dy = -newBall.dy;
        }

        const paddleY = GAME_HEIGHT - 50;
        if (newBall.dy > 0 && 
            newBall.y + BALL_SIZE >= paddleY && 
            newBall.y <= paddleY + PADDLE_HEIGHT &&
            newBall.x + BALL_SIZE >= paddle.x && 
            newBall.x <= paddle.x + paddle.width) {
          newBall.dy = -Math.abs(newBall.dy);
        }

        if (newBall.y >= GAME_HEIGHT) {
          setGameActive(false);
          setGameOver(true);
        }

        return newBall;
      });

      setPowerUps(p => {
        const newPowerUps = p
          .map(pu => ({ ...pu, y: pu.y + 2 }))
          .filter(pu => {
            const paddleY = GAME_HEIGHT - 50;
            if (pu.y + 20 >= paddleY && pu.y <= paddleY + PADDLE_HEIGHT &&
                pu.x + 20 >= paddle.x && pu.x <= paddle.x + paddle.width) {
              if (pu.type === 'expand') {
                setPaddle(p => ({ ...p, width: Math.min(150, p.width + 30) }));
              } else if (pu.type === 'slow') {
                setBall(b => ({ ...b, dx: b.dx * 0.7, dy: b.dy * 0.7 }));
              }
              return false;
            }
            return pu.y < GAME_HEIGHT;
          });
        return newPowerUps;
      });

      setBricks(currentBricks => {
        const newBricks = [...currentBricks];
        let hitBrick = false;

        newBricks.forEach(brick => {
          if (!brick.destroyed) {
            if (ball.x + BALL_SIZE > brick.x && ball.x < brick.x + brick.width &&
                ball.y + BALL_SIZE > brick.y && ball.y < brick.y + brick.height) {
              brick.destroyed = true;
              hitBrick = true;
              setScore(s => s + 10);

              if (brick.hasPowerUp) {
                setPowerUps(p => [...p, {
                  x: brick.x,
                  y: brick.y,
                  type: Math.random() < 0.5 ? 'expand' : 'slow',
                  id: Date.now()
                }]);
              }
            }
          }
        });

        if (hitBrick) {
          setBall(b => ({ ...b, dy: -b.dy }));
          if (newBricks.every(brick => brick.destroyed)) {
            setGameActive(false);
            setGameWon(true);
          }
        }

        return newBricks;
      });

      animationRef.current = requestAnimationFrame(gameLoop);
    };

    animationRef.current = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(animationRef.current);
  }, [gameActive, gameOver, gameWon, keys, paddle, ball]);

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">🧱 Breakout Advanced</h1>
        <div className="game-instructions">
          Use A/D or ← → to move. Catch power-ups for bonuses!
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">Score: {score}</div>
        
        <div className="game-play-area">
          <div ref={gameAreaRef} className="breakout-game-area">
            {bricks.map((brick, i) => (
              !brick.destroyed && (
                <div key={i} className="breakout-brick" style={{
                  left: brick.x, top: brick.y,
                  width: brick.width, height: brick.height,
                  background: brick.color
                }}>
                  {brick.hasPowerUp && '⭐'}
                </div>
              )
            ))}
            
            {powerUps.map(pu => (
              <div key={pu.id} className="power-up" style={{ left: pu.x, top: pu.y }}>
                {pu.type === 'expand' ? '📏' : '🐌'}
              </div>
            ))}
            
            <div className="breakout-ball" style={{ left: ball.x, top: ball.y }} />
            <div className="breakout-paddle" style={{ left: paddle.x, width: paddle.width }} />

            {!gameActive && (
              <div className="game-status-overlay">
                {gameWon && <div className="status-message win">🎉 You Won!</div>}
                {gameOver && <div className="status-message lose">💥 Game Over!</div>}
                {!gameWon && !gameOver && <div className="status-message start">Press Start!</div>}
              </div>
            )}
          </div>
        </div>

        <div className="game-controls">
          <button className="btn btn-primary" onClick={startGame}>
            {gameOver || gameWon ? 'Play Again' : 'Start Game'}
          </button>
          <Link to="/" className="btn btn-secondary">Back to Home</Link>
        </div>
      </div>
    </div>
  );
};

export default BreakoutAdvanced;
